import { ThemeColourToken } from '~constants/theming';

/**
 * Component tokens: the per-component colour layer that sits between the theme
 * tokens and the markup.
 *
 * Theme tokens (`surface.primary_support`, …) are shared by everything, so
 * repointing one to satisfy a single customer changes every component that uses
 * it. A component token is owned by one component, defaults to the theme token
 * that component already used, and can be overridden on its own.
 *
 * Adding one is three steps:
 *   1. declare the CSS custom property in `src/index.css`, defaulting to the
 *      theme token the component uses today;
 *   2. add it here, so the name is type-checked;
 *   3. swap the component's Tailwind class over (`bg-surface-x` → `bg-page-x`).
 *
 * Nothing else changes: with no override present the token resolves to the same
 * theme token as before, branding included.
 */
export const COMPONENT_TOKEN_CSS_VARS = {
  /** Background of the whole dashboard — <body> and the base layout section. */
  page_background: '--color-page-background',
} as const satisfies Record<string, `--color-${string}`>;

export type ComponentToken = keyof typeof COMPONENT_TOKEN_CSS_VARS;

/**
 * An override either points the token at a different theme token — so it keeps
 * following branding — or pins it to a literal CSS colour.
 */
export type ComponentTokenOverride = { themeToken: ThemeColourToken } | { colour: string };

export type ComponentTokenOverrides = Partial<Record<ComponentToken, ComponentTokenOverride>>;

export interface CustomerComponentTokens {
  /**
   * The customer's name, for whoever reads this next. Matching is on the id alone,
   * so this is free to be a label — but keep it as the `customer` claim spells it,
   * so it can be grepped against a token.
   */
  name: string;
  tokens: ComponentTokenOverrides;
}

/**
 * Per-customer component token overrides, keyed by the numeric `customer_id` claim
 * on the action token (see `getAuthContextFromJwt`).
 *
 * The id is the key rather than the `customer` name because the name is a display
 * label and can be renamed, and a key that no longer matches fails silently — the
 * override simply does not apply. The id also covers every tenant a customer has,
 * so an override survives in dev and staging instead of only production.
 *
 * A customer with no entry gets the defaults from `src/index.css`. To find a
 * customer's id, decode any of their action tokens, or look it up in
 * customer-admin-tool.
 */
export const CUSTOMER_COMPONENT_TOKENS: Record<number, CustomerComponentTokens> = {
  516: {
    name: 'GOLDEN-NATURALS',
    // They embed the dashboard and paint the background themselves, so it has to
    // show through rather than sit on our own surface colour.
    tokens: { page_background: { colour: 'transparent' } },
  },
};
